import { InjectiveMegaVaultRpc } from '@injectivelabs/indexer-proto-ts';
import { UnspecifiedErrorCode, GrpcUnaryRequestException, } from '@injectivelabs/exceptions';
import { IndexerModule } from '../types/index.js';
import BaseGrpcConsumer from '../../base/BaseIndexerGrpcConsumer.js';
import { IndexerGrpcMegaVaultTransformer } from '../transformers/index.js';
/**
 * @category Indexer Grpc API
 */
export class IndexerGrpcMegaVaultApi extends BaseGrpcConsumer {
    module = IndexerModule.MegaVault;
    client;
    constructor(endpoint) {
        super(endpoint);
        this.client = new InjectiveMegaVaultRpc.InjectiveMegavaultRPCClientImpl(this.getGrpcWebImpl(endpoint));
    }
    async fetchVault({ vaultAddress }) {
        const request = InjectiveMegaVaultRpc.GetVaultRequest.create();
        request.vaultAddress = vaultAddress;
        try {
            const response = await this.retry(() => this.client.GetVault(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.vaultResponseToVault(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchVault',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchVault',
                contextModule: this.module,
            });
        }
    }
    async fetchVaultUser({ vaultAddress, userAddress, }) {
        const request = InjectiveMegaVaultRpc.GetUserRequest.create();
        request.vaultAddress = vaultAddress;
        request.userAddress = userAddress;
        try {
            const response = await this.retry(() => this.client.GetUser(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.userResponseToUser(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchVaultUser',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchVaultUser',
                contextModule: this.module,
            });
        }
    }
    async fetchVaultSubscriptions({ token, status, perPage, userAddress, vaultAddress, }) {
        const request = InjectiveMegaVaultRpc.ListSubscriptionsRequest.create();
        request.vaultAddress = vaultAddress;
        request.userAddress = userAddress;
        if (status) {
            request.status = status;
        }
        if (perPage) {
            request.perPage = perPage;
        }
        if (token) {
            request.token = token;
        }
        try {
            const response = await this.retry(() => this.client.ListSubscriptions(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.subscriptionsResponseToSubscriptions(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchVaultSubscriptions',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchVaultSubscriptions',
                contextModule: this.module,
            });
        }
    }
    async fetchVaultRedemptions({ token, status, perPage, userAddress, vaultAddress, }) {
        const request = InjectiveMegaVaultRpc.ListRedemptionsRequest.create();
        request.vaultAddress = vaultAddress;
        request.userAddress = userAddress;
        if (status) {
            request.status = status;
        }
        if (perPage) {
            request.perPage = perPage;
        }
        if (token) {
            request.token = token;
        }
        try {
            const response = await this.retry(() => this.client.ListRedemptions(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.redemptionsResponseToRedemptions(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchVaultRedemptions',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchVaultRedemptions',
                contextModule: this.module,
            });
        }
    }
    async fetchOperatorRedemptionBuckets({ vaultAddress, operatorAddress, }) {
        const request = InjectiveMegaVaultRpc.GetOperatorRedemptionBucketsRequest.create();
        request.vaultAddress = vaultAddress;
        request.operatorAddress = operatorAddress;
        try {
            const response = await this.retry(() => this.client.GetOperatorRedemptionBuckets(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.operatorRedemptionBucketsResponseToOperatorRedemptionBuckets(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchOperatorRedemptionBuckets',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchOperatorRedemptionBuckets',
                contextModule: this.module,
            });
        }
    }
    async fetchVaultTvlHistory({ since, vaultAddress, maxDataPoints, }) {
        const request = InjectiveMegaVaultRpc.TvlHistoryRequest.create();
        request.vaultAddress = vaultAddress;
        request.since = since.toString();
        if (maxDataPoints) {
            request.maxDataPoints = maxDataPoints;
        }
        try {
            const response = await this.retry(() => this.client.TvlHistory(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.tvlHistoryResponseToTvlHistory(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchVaultTvlHistory',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchVaultTvlHistory',
                contextModule: this.module,
            });
        }
    }
    async fetchVaultPnlHistory({ since, vaultAddress, maxDataPoints, }) {
        const request = InjectiveMegaVaultRpc.PnlHistoryRequest.create();
        request.vaultAddress = vaultAddress;
        request.since = since.toString();
        if (maxDataPoints) {
            request.maxDataPoints = maxDataPoints;
        }
        try {
            const response = await this.retry(() => this.client.PnlHistory(request, this.metadata));
            return IndexerGrpcMegaVaultTransformer.pnlHistoryResponseToPnlHistory(response);
        }
        catch (e) {
            if (e instanceof InjectiveMegaVaultRpc.GrpcWebError) {
                throw new GrpcUnaryRequestException(new Error(e.toString()), {
                    code: e.code,
                    context: 'fetchVaultPnlHistory',
                    contextModule: this.module,
                });
            }
            throw new GrpcUnaryRequestException(e, {
                code: UnspecifiedErrorCode,
                context: 'fetchVaultPnlHistory',
                contextModule: this.module,
            });
        }
    }
}
